import { store } from '@/store';
import type { AppDispatch } from '@/store/types';
import { analyticsActions } from '@/store/analytics';
import { calendarActions } from '@/store/calendar';
import { caregiverActions } from '@/store/caregiver';
import { communityActions } from '@/store/community';
import { documentActions } from '@/store/document';
import { healthCareWorkerActions } from '@/store/healthCareWorker';
import { infantActions } from '@/store/infant';
import { motherActions } from '@/store/mother';
import { notificationActions } from '@/store/notifications';
import { userActions } from '@/store/user';

/** Offline credential keys written on successful online login. */
export const OFFLINE_CREDENTIAL_KEYS = [
  'userIdHash',
  'userNameHash',
  'userHash',
  'userTokenHash',
  'userLocalxpiration',
] as const;

/**
 * Clears device-local offline login credentials.
 * Call when switching users so the previous user's password/token cannot be reused.
 */
export function clearOfflineCredentials(): void {
  OFFLINE_CREDENTIAL_KEYS.forEach((key) => localStorage.removeItem(key));
}

/**
 * Clears user-scoped Redux state (profile, clients, visits-related bags, etc.).
 *
 * Intentionally does NOT clear static/reference data (genders, visit types, settings, …)
 * so offline-first same-user re-login stays cheap.
 *
 * Call this when the authenticated user id differs from the cached profile user id.
 */
export function clearUserScopedStore(
  dispatch: AppDispatch = store.dispatch
): void {
  dispatch(userActions.resetUserState());
  dispatch(caregiverActions.resetCaregiverState());
  dispatch(motherActions.resetMotherState());
  dispatch(infantActions.resetInfantState());
  dispatch(healthCareWorkerActions.resetHealthCareWorkerState());
  dispatch(documentActions.resetDocumentsState());
  dispatch(calendarActions.resetCalendarState());
  dispatch(notificationActions.resetNotificationState());
  dispatch(communityActions.resetCommunityState());
  dispatch(analyticsActions.resetAnalyticsState());
}

/**
 * Returns true when persisted offline profile belongs to a different user
 * than the one who just authenticated.
 */
export function isCachedUserDifferentFrom(
  authUserId: string | undefined
): boolean {
  if (!authUserId) return false;
  const cachedUserId = store.getState().user?.user?.id;
  return Boolean(cachedUserId && cachedUserId !== authUserId);
}

/**
 * If offline data belongs to another user, wipe it so the new session cannot
 * show the previous CHW's profile/clients. Same-user re-login keeps the bag.
 */
export function clearUserScopedStoreIfUserChanged(
  authUserId: string | undefined
): boolean {
  if (!isCachedUserDifferentFrom(authUserId)) {
    return false;
  }
  clearUserScopedStore();
  return true;
}
